<script setup lang="ts">
  import DOMPurify from 'dompurify';
  import { ref, watch } from 'vue';

  const props = defineProps<{
    url: string;
    fileName: string;
    mimeType?: string | null;
  }>();

  const sanitizedHtml = ref<string>('');
  const loading = ref(false);
  const error = ref<string | null>(null);

  const loadAndSanitizeHtml = async () => {
    loading.value = true;
    error.value = null;

    try {
      const response = await fetch(props.url);
      if (!response.ok) throw new Error('Failed to load file');
      const htmlContent = await response.text();

      // Sanitize HTML with DOMPurify
      sanitizedHtml.value = DOMPurify.sanitize(htmlContent, {
        WHOLE_DOCUMENT: true,
        ADD_TAGS: ['style', 'link'],
        ADD_ATTR: ['colspan', 'rowspan', 'valign', 'align', 'id', 'name', 'type'],
        ALLOW_DATA_ATTR: false,
      });
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Failed to load file';
    } finally {
      loading.value = false;
    }
  };

  watch(() => props.url, loadAndSanitizeHtml, { immediate: true });
</script>

<template>
  <div class="flex h-full flex-col">
    <div v-if="loading" class="flex items-center justify-center p-8">
      <span class="text-muted-foreground">Loading...</span>
    </div>

    <div v-else-if="error" class="flex items-center justify-center p-8">
      <span class="text-destructive">{{ error }}</span>
    </div>

    <iframe
      v-else
      :srcdoc="sanitizedHtml"
      sandbox=""
      class="w-full min-h-[600px] flex-1"
      :title="fileName"
    />
  </div>
</template>
